import type { OverrideMovementNote, SubscriptionsSummary } from '../../api/finance';

/**
 * The part of this month's net new MRR that reporting overrides explain while
 * Odoo's contracts stood still (Oct 26: Xerox's reduced rate running out
 * +€4,833 "rate restored"; ISS and Swiss GRC "override changed" −€315). Read
 * straight from the API's per-month split, the same fields Finance's Net new
 * MRR tile reads, so Home and Finance quote one number. Null when the API has
 * no split for the month.
 */
export function overrideSummary(
  summary: Pick<SubscriptionsSummary, 'overrideSplits' | 'overrideMovements' | 'bridge'>,
): { month: string; restored: number; changed: number; net: number; moves: OverrideMovementNote[] } | null {
  const month = summary.bridge.month;
  const split = summary.overrideSplits?.[month];
  if (!split) return null;
  return {
    month,
    restored: split.restoredMrr,
    changed: split.overrideChangedMrr,
    net: split.restoredMrr + split.overrideChangedMrr,
    moves: summary.overrideMovements.filter((n) => n.month === month && n.kind !== null),
  };
}

/** Days ahead the Renewals tile counts, matching Finance's contract-expiry rule. */
export const RENEWAL_WINDOW_DAYS = 30;

/**
 * Contracts ending in the next `days`, by the same `expiryDate`/`daysLeft`
 * Finance's Contract expiries card reads (Arendt ends 31 Oct, not "1 Nov").
 */
export function contractsEnding<T extends { expiryDate: string | null; daysLeft: number | null }>(
  contracts: T[],
  days: number,
): T[] {
  return contracts
    .filter((c) => c.expiryDate !== null && c.daysLeft !== null && c.daysLeft >= 0 && c.daysLeft <= days)
    .sort((a, b) => a.daysLeft! - b.daysLeft!);
}

/** "+€7.5k", "−€315", one decimal from €1k so the three parts line up. */
export function signedCompactMoney(n: number): string {
  const sign = n > 0 ? '+' : n < 0 ? '−' : '';
  const abs = Math.abs(n);
  const body = abs >= 1_000_000 ? `${(abs / 1_000_000).toFixed(2)}M` : abs >= 1_000 ? `${(abs / 1_000).toFixed(1)}k` : `${Math.round(abs)}`;
  return `${sign}€${body}`;
}

/**
 * The MRR tile's movement, labelled with its period: "+€7.5k net new in Oct",
 * then "new +€3.0k · sheet rates +€4.5k" when rate changes in the MRR
 * sheet (Finance's "Rate restored" and "Override changed") explain part of it.
 * New business is net new minus what the sheet explains, so the parts add up.
 * `month` is the month Finance's Net new MRR tile covers (to date).
 */
export function mrrMovementLine(
  netNew: number,
  overridesNet: number | null,
  month: string,
): { headline: string; parts: string | null } {
  return {
    headline: `${signedCompactMoney(netNew)} net new in ${month}`,
    parts:
      overridesNet === null
        ? null
        : `new ${signedCompactMoney(netNew - overridesNet)} · sheet rates ${signedCompactMoney(overridesNet)}`,
  };
}
